import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const HeroBanner = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [popularSchools, setPopularSchools] = useState([]);
    const [stats, setStats] = useState(null);
    const navigate = useNavigate();

    // Fetch popular schools from API
    useEffect(() => {
        const fetchPopular = async () => {
            try {
                const response = await fetch(`${API_URL}/api/schools/popular?limit=4`);
                if (response.ok) {
                    const data = await response.json();
                    setPopularSchools(data.schools || []);
                }
            } catch (error) {
                console.error('Error fetching popular schools:', error);
            }
        };
        fetchPopular();
    }, []);

    // Fetch stats from API
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`${API_URL}/api/schools/stats`);
                if (response.ok) {
                    const data = await response.json();
                    setStats(data);
                }
            } catch (error) {
                console.error('Error fetching stats:', error);
            }
        };
        fetchStats();
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        
        setIsSearching(true);
        setTimeout(() => {
            setIsSearching(false);
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }, 300);
    };

    const handlePopularSearch = (school) => {
        const searchTerm = school.name;
        setSearchQuery(searchTerm);
        navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M+';
        if (num >= 1000) return (num / 1000).toFixed(0) + 'K+';
        return num?.toString() || '0';
    };

    return (
        <section className="relative overflow-hidden bg-accent py-8 sm:py-12">
            {/* Decorative background pattern - Web 2.0 style */}
            <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 left-0 w-32 h-32 bg-accent-green/20 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-40 h-40 bg-accent-yellow/20 rounded-full blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
            </div>

            <div className="relative max-w-[1400px] mx-auto px-4">
                <div className="text-center mb-8">
                    {/* Main heading with nostalgic font */}
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3 leading-tight">
                        Odnajdź swoją klasę{' '}
                        <span className="text-primary">–</span>{' '}
                        <span className="text-accent-green">dawną</span>{' '}
                        <span className="text-primary">czy</span>{' '}
                        <span className="text-accent-yellow">nową!</span>
                    </h1>
                    
                    <p className="text-muted-foreground text-base sm:text-lg max-w-xl mx-auto font-body">
                        Połącz się z kolegami i koleżankami ze szkolnej ławki. 
                        Ponad <span className="font-semibold text-primary">{formatNumber(stats?.total_schools || 37000)}</span> szkół w bazie!
                    </p>
                </div>

                {/* Search form - Web 2.0 style */}
                <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-6">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Wpisz nazwę szkoły, miasta lub klasy..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="nk-input pl-12 h-12 text-base rounded-xl"
                            />
                        </div>
                        <Button 
                            type="submit" 
                            disabled={isSearching}
                            className="nk-button h-12 px-8 text-base rounded-xl"
                        >
                            {isSearching ? (
                                <span className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 animate-spin" />
                                    Szukam...
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <Search className="w-4 h-4" />
                                    Szukaj
                                </span>
                            )}
                        </Button>
                    </div>
                </form>

                {/* Popular searches - real schools from database */}
                {popularSchools.length > 0 && (
                    <div className="text-center">
                        <span className="text-sm text-muted-foreground">Popularne szkoły: </span>
                        <div className="inline-flex flex-wrap justify-center gap-x-3 gap-y-1 mt-1">
                            {popularSchools.map((school) => (
                                <button
                                    key={school.id}
                                    onClick={() => handlePopularSearch(school)}
                                    className="text-sm text-primary hover:text-primary-light hover:underline transition-colors"
                                >
                                    {school.name.length > 40 ? school.name.substring(0, 40) + '...' : school.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Stats banner - real data from API. min-h zapobiega CLS gdy fetch przelicza wartosci. */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 max-w-3xl mx-auto">
                    {[
                        { value: formatNumber(stats?.total_users || 250000), label: 'Użytkowników' },
                        { value: formatNumber(stats?.total_schools || 37000), label: 'Szkół' },
                        { value: formatNumber(stats?.total_classes || 45000), label: 'Klas' },
                        { value: formatNumber(stats?.total_photos || 1000000), label: 'Zdjęć' },
                    ].map((stat) => (
                        <div
                            key={stat.label}
                            className="nk-box text-center py-3 px-2 hover:shadow-nk-glow transition-shadow cursor-default min-h-[76px] flex flex-col items-center justify-center"
                        >
                            <div className="font-display text-xl sm:text-2xl font-bold text-primary leading-tight">
                                {stat.value}
                            </div>
                            <div className="text-xs sm:text-sm text-muted-foreground">
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
